import { useEffect, useRef, type ReactNode } from 'react';
import { S, t, type Leaf, type Locale } from '../../i18n/strings';
import styles from './xp.module.css';

export interface StartEntry {
  readonly id: string;
  readonly label: Leaf;
  readonly onSelect: () => void;
}

interface Props {
  readonly lang: Locale;
  /** Programmes, colonne blanche de gauche. */
  readonly programs: readonly StartEntry[];
  /** Dossiers, colonne bleue de droite. */
  readonly places: readonly StartEntry[];
  /** Les deux entrées du bandeau bleu du bas. */
  readonly footer: readonly StartEntry[];
  readonly onClose: () => void;
}

/**
 * Le menu démarrer.
 *
 * Deux colonnes comme le vrai — programmes à gauche, dossiers à droite — et
 * **aucune entrée morte** : chacune emmène quelque part dans la page ou ouvre
 * une boîte de dialogue. Un menu démarrer qu'on ne peut qu'ouvrir et refermer
 * serait pire que pas de menu du tout.
 *
 * Il se referme au clic ailleurs, à `Échap`, et après chaque choix.
 */
export function XpStartMenu({ lang, programs, places, footer, onClose }: Props): ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstRef.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    // `pointerdown` plutôt que `click` : sinon le clic qui a ouvert le menu le
    // referme aussitôt en remontant.
    const onPointer = (event: PointerEvent): void => {
      const node = rootRef.current;
      if (node && event.target instanceof Node && !node.contains(event.target)) onClose();
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [onClose]);

  const entry = (item: StartEntry, index: number): ReactNode => (
    <button
      key={item.id}
      ref={index === 0 ? firstRef : undefined}
      type="button"
      role="menuitem"
      className={styles.startItem}
      onClick={() => {
        onClose();
        item.onSelect();
      }}
    >
      <span className={styles.startIcon} aria-hidden="true" />
      <span>{t(item.label, lang)}</span>
    </button>
  );

  return (
    <div
      className={styles.startMenu}
      ref={rootRef}
      role="menu"
      aria-label={t(S.xp.chrome.start_menu, lang)}
    >
      <div className={styles.startHeader}>
        <span className={styles.startAvatar} aria-hidden="true" />
        <span>{t(S.xp.start.user, lang)}</span>
      </div>

      <div className={styles.startColumns}>
        <div className={styles.startLeft}>{programs.map(entry)}</div>
        <div className={styles.startRight}>{places.map((item) => entry(item, 1))}</div>
      </div>

      <div className={styles.startFooter}>
        {footer.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className={styles.startFooterItem}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
          >
            <span className={styles.startFooterIcon} aria-hidden="true" />
            <span>{t(item.label, lang)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
